import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Send as SendIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Login as LoginIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  TouchApp as TouchAppIcon,
} from '@mui/icons-material'
import {
  emailDeliveryService,
  EmailDeliveryLog,
  EmailDeliveryStats,
  RegistrationSummary,
  EmailDeliveryFilters,
  EmailDeliveryStatus,
  EmailType,
  EMAIL_DELIVERY_STATUS_LABELS,
  EMAIL_TYPE_LABELS,
  EMAIL_DELIVERY_STATUS_COLORS,
} from '../services/emailDeliveryService'
import { SearchField } from '../components/common/SearchField'

const formatDate = (dateString?: string, locale: string = 'ja-JP'): string => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString(locale)
}

interface StatCardProps {
  title: string
  value: number
  icon: React.ReactNode
  color?: string
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color = '#2196F3' }) => (
  <Card sx={{ minWidth: 140, flex: 1 }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
)

export const EmailDeliveryPage: React.FC = () => {
  const { t, i18n } = useTranslation('emailDelivery')
  const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
  const [logs, setLogs] = useState<EmailDeliveryLog[]>([])
  const [stats, setStats] = useState<EmailDeliveryStats | null>(null)
  const [registrationSummary, setRegistrationSummary] = useState<RegistrationSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<EmailDeliveryStatus | ''>('')
  const [typeFilter, setTypeFilter] = useState<EmailType | ''>('')
  const [selectedLog, setSelectedLog] = useState<EmailDeliveryLog | null>(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const filters: EmailDeliveryFilters = {
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
        emailType: typeFilter || undefined,
      }

      const [logsResponse, summaryResponse] = await Promise.all([
        emailDeliveryService.getDeliveryLogs(filters),
        emailDeliveryService.getRegistrationSummary(),
      ])

      setLogs(logsResponse.logs)
      setTotal(logsResponse.pagination.total)
      setStats(logsResponse.stats)
      setRegistrationSummary(summaryResponse)
    } catch (error) {
      console.error('データ取得エラー:', error)
      setSnackbar({ open: true, message: t('messages.loadError'), severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, search, statusFilter, typeFilter, t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value)
    setPage(0)
  }

  const handleTypeFilterChange = (event: any) => {
    setTypeFilter(event.target.value)
    setPage(0)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleOpenDetail = (log: EmailDeliveryLog) => {
    setSelectedLog(log)
    setDetailDialogOpen(true)
  }

  const handleCheckTimeouts = async () => {
    try {
      const result = await emailDeliveryService.checkTimeouts()
      setSnackbar({ open: true, message: result.message, severity: 'success' })
      fetchData()
    } catch (error) {
      setSnackbar({ open: true, message: t('messages.timeoutCheckError'), severity: 'error' })
    }
  }

  const getStatusIcon = (status: EmailDeliveryStatus): React.ReactNode => {
    switch (status) {
      case 'PENDING':
        return <ScheduleIcon fontSize="small" />
      case 'SENT':
        return <SendIcon fontSize="small" />
      case 'DELIVERED':
        return <MarkEmailReadIcon fontSize="small" />
      case 'OPENED':
        return <VisibilityIcon fontSize="small" />
      case 'CLICKED':
        return <TouchAppIcon fontSize="small" />
      case 'BOUNCED':
      case 'DROPPED':
      case 'FAILED':
        return <ErrorIcon fontSize="small" />
      case 'DEFERRED':
      case 'SPAM_REPORT':
      case 'UNSUBSCRIBED':
        return <WarningIcon fontSize="small" />
      default:
        return <EmailIcon fontSize="small" />
    }
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Typography variant="h4" component="h1" gutterBottom>
        {t('title')}
      </Typography>

      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {t('subtitle')}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ScheduleIcon />} onClick={handleCheckTimeouts}>
              {t('actions.checkTimeouts')}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>
              {t('actions.refresh')}
            </Button>
          </Stack>
        </Stack>

        {/* 登録メールサマリー */}
        {registrationSummary && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon /> {t('summary.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <StatCard
                title={t('summary.sent')}
                value={registrationSummary.totalSent}
                icon={<SendIcon />}
                color="#2196F3"
              />
              <StatCard
                title={t('summary.delivered')}
                value={registrationSummary.delivered}
                icon={<MarkEmailReadIcon />}
                color="#4CAF50"
              />
              <StatCard
                title={t('summary.opened')}
                value={registrationSummary.opened}
                icon={<VisibilityIcon />}
                color="#00BCD4"
              />
              <StatCard
                title={t('summary.loggedIn')}
                value={registrationSummary.loggedIn}
                icon={<LoginIcon />}
                color="#8BC34A"
              />
              <StatCard
                title={t('summary.notLoggedIn')}
                value={registrationSummary.notLoggedIn}
                icon={<ScheduleIcon />}
                color="#FF9800"
              />
              <StatCard
                title={t('summary.problematic')}
                value={registrationSummary.problematic}
                icon={<ErrorIcon />}
                color="#f44336"
              />
            </Box>
          </Paper>
        )}

        {/* 全体統計 */}
        {stats && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('stats.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`${t('stats.total')}: ${stats.total}`} color="default" />
              <Chip label={`${t('stats.sent')}: ${stats.sent}`} color="primary" />
              <Chip label={`${t('stats.delivered')}: ${stats.delivered}`} color="primary" />
              <Chip label={`${t('stats.opened')}: ${stats.opened}`} color="secondary" />
              <Chip label={`${t('stats.clicked')}: ${stats.clicked}`} color="secondary" />
              <Chip label={`${t('stats.bounced')}: ${stats.bounced}`} color="error" />
              <Chip label={`${t('stats.failed')}: ${stats.failed}`} color="error" />
            </Box>
          </Paper>
        )}

        {/* フィルター */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <SearchField
              value={search}
              onChange={handleSearchChange}
              placeholder={t('filter.searchPlaceholder')}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('filter.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('filter.status')}
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="">{t('filter.all')}</MenuItem>
                {(Object.keys(EMAIL_DELIVERY_STATUS_LABELS) as EmailDeliveryStatus[]).map(
                  (status) => (
                    <MenuItem key={status} value={status}>
                      {EMAIL_DELIVERY_STATUS_LABELS[status]}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('filter.emailType')}</InputLabel>
              <Select
                value={typeFilter}
                label={t('filter.emailType')}
                onChange={handleTypeFilterChange}
              >
                <MenuItem value="">{t('filter.all')}</MenuItem>
                {(Object.keys(EMAIL_TYPE_LABELS) as EmailType[]).map((type) => (
                  <MenuItem key={type} value={type}>
                    {EMAIL_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* テーブル */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('table.sentAt')}</TableCell>
                  <TableCell>{t('table.type')}</TableCell>
                  <TableCell>{t('table.recipient')}</TableCell>
                  <TableCell>{t('table.subject')}</TableCell>
                  <TableCell>{t('table.deliveryStatus')}</TableCell>
                  <TableCell>{t('table.loginStatus')}</TableCell>
                  <TableCell align="center">{t('table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">{t('table.noData')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(log.sentAt || log.createdAt, locale)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={EMAIL_TYPE_LABELS[log.emailType]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.emailAddress}</Typography>
                        {log.user && (
                          <Typography variant="caption" color="text.secondary">
                            {log.user.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={log.subject}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {log.subject}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(log.status) as React.ReactElement}
                          label={EMAIL_DELIVERY_STATUS_LABELS[log.status]}
                          size="small"
                          color={EMAIL_DELIVERY_STATUS_COLORS[log.status]}
                        />
                        {log.isTimedOut && (
                          <Chip
                            label={t('table.timedOut')}
                            size="small"
                            color="secondary"
                            sx={{ ml: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {log.emailType === 'USER_REGISTRATION' && (
                          <Chip
                            icon={log.userLoggedIn ? <CheckCircleIcon /> : <ScheduleIcon />}
                            label={log.userLoggedIn ? t('table.loggedIn') : t('table.notLoggedIn')}
                            size="small"
                            color={log.userLoggedIn ? 'success' : 'default'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDetail(log)}>
                          <OpenInNewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage={t('table.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `${to}+`}`
            }
          />
        </Paper>
      </Stack>

      {/* 詳細ダイアログ */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('detail.title')}</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* 基本情報 */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('detail.basicInfo')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('detail.recipient')}
                    </Typography>
                    <Typography>{selectedLog.emailAddress}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('detail.type')}
                    </Typography>
                    <Typography>{EMAIL_TYPE_LABELS[selectedLog.emailType]}</Typography>
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('detail.subject')}
                    </Typography>
                    <Typography>{selectedLog.subject}</Typography>
                  </Box>
                </Box>
              </Paper>

              {/* ステータス情報 */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('detail.deliveryStatus')}
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <SendIcon color={selectedLog.sentAt ? 'success' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('detail.sent')}
                      secondary={formatDate(selectedLog.sentAt, locale)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <MarkEmailReadIcon color={selectedLog.deliveredAt ? 'success' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('detail.delivered')}
                      secondary={formatDate(selectedLog.deliveredAt, locale)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <VisibilityIcon color={selectedLog.openedAt ? 'success' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('detail.opened')}
                      secondary={formatDate(selectedLog.openedAt, locale)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TouchAppIcon color={selectedLog.clickedAt ? 'success' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('detail.clicked')}
                      secondary={formatDate(selectedLog.clickedAt, locale)}
                    />
                  </ListItem>
                  {selectedLog.emailType === 'USER_REGISTRATION' && (
                    <ListItem>
                      <ListItemIcon>
                        <LoginIcon color={selectedLog.userLoggedIn ? 'success' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('detail.login')}
                        secondary={
                          selectedLog.userLoggedIn
                            ? formatDate(selectedLog.userLoggedInAt, locale)
                            : t('detail.notLoggedIn')
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>

              {/* エラー情報 */}
              {(selectedLog.errorCode || selectedLog.errorMessage || selectedLog.bounceReason) && (
                <Alert severity="error">
                  <Typography variant="subtitle2">{t('detail.errorInfo')}</Typography>
                  {selectedLog.errorCode && (
                    <Typography variant="body2">
                      {t('detail.errorCode')}: {selectedLog.errorCode}
                    </Typography>
                  )}
                  {selectedLog.errorMessage && (
                    <Typography variant="body2">
                      {t('detail.errorMessage')}: {selectedLog.errorMessage}
                    </Typography>
                  )}
                  {selectedLog.bounceType && (
                    <Typography variant="body2">
                      {t('detail.bounceType')}: {selectedLog.bounceType}
                    </Typography>
                  )}
                  {selectedLog.bounceReason && (
                    <Typography variant="body2">
                      {t('detail.bounceReason')}: {selectedLog.bounceReason}
                    </Typography>
                  )}
                </Alert>
              )}

              {/* ユーザー情報 */}
              {selectedLog.user && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {t('detail.userInfo')}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('detail.name')}
                      </Typography>
                      <Typography>{selectedLog.user.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('detail.email')}
                      </Typography>
                      <Typography>{selectedLog.user.email}</Typography>
                    </Box>
                    {selectedLog.user.department && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('detail.department')}
                        </Typography>
                        <Typography>{selectedLog.user.department}</Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('detail.firstLogin')}
                      </Typography>
                      <Typography>
                        {selectedLog.user.firstLogin
                          ? t('detail.firstLoginPending')
                          : t('detail.firstLoginComplete')}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* メタ情報 */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('detail.systemInfo')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('detail.sendgridMessageId')}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {selectedLog.sendgridMessageId || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('detail.lastWebhook')}
                    </Typography>
                    <Typography>{formatDate(selectedLog.lastWebhookAt, locale)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('detail.createdAt')}
                    </Typography>
                    <Typography>{formatDate(selectedLog.createdAt, locale)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('detail.updatedAt')}
                    </Typography>
                    <Typography>{formatDate(selectedLog.updatedAt, locale)}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t('common:buttons.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default EmailDeliveryPage
